import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Checkbox } from "../../ui/checkbox";
import { Slider } from "../../ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { LockedPreview } from "../LockedPreview";
import { VoiceHint } from "../VoiceHint";
import { MasterChefUnlockCard } from "../../MasterChefUnlockCard";
import { Users, ChefHat, Clock, Home, Rocket } from "lucide-react";
import { IconWrapper } from "../IconWrapper";
import { equipmentIcons, getBatchPrepIcon } from "../iconMappings";

interface HouseholdProfile {
  householdSize: number;
  equipment: string[];
  batchPrep: string;
  familyScaling: number;
}

interface HouseholdEquipmentSectionProps {
  profile: HouseholdProfile;
  onUpdate: (updates: Partial<HouseholdProfile>) => void;
  currentTier: 'demo' | 'starter' | 'core' | 'core_plus' | 'pro';
  isLocked: boolean;
  onUpgrade?: () => void;
}

export function HouseholdEquipmentSection({ 
  profile, 
  onUpdate, 
  currentTier, 
  isLocked,
  onUpgrade = () => console.log("Upgrade to Master Chef")
}: HouseholdEquipmentSectionProps) {
  
  if (isLocked) {
    return (
      <MasterChefUnlockCard
        userTier={currentTier}
        onUpgrade={onUpgrade}
      />
    );
  }

  const equipmentOptions = [
    { id: "oven", label: "Oven", icon: equipmentIcons.oven },
    { id: "stovetop", label: "Stovetop", icon: equipmentIcons.stovetop },
    { id: "microwave", label: "Microwave", icon: equipmentIcons.microwave },
    { id: "air-fryer", label: "Air Fryer", icon: equipmentIcons['air-fryer'] },
    { id: "slow-cooker", label: "Slow Cooker", icon: equipmentIcons['slow-cooker'] },
    { id: "instant-pot", label: "Instant Pot", icon: equipmentIcons['instant-pot'] },
    { id: "rice-cooker", label: "Rice Cooker", icon: equipmentIcons['rice-cooker'] },
    { id: "blender", label: "Blender", icon: equipmentIcons.blender },
    { id: "food-processor", label: "Food Processor", icon: equipmentIcons['food-processor'] },
    { id: "grill", label: "Grill/BBQ", icon: equipmentIcons.grill },
    { id: "stand-mixer", label: "Stand Mixer", icon: equipmentIcons['stand-mixer'] },
    { id: "immersion-blender", label: "Immersion Blender", icon: equipmentIcons['immersion-blender'] }
  ];

  const toggleEquipment = (equipment: string) => {
    const current = profile.equipment || [];
    if (current.includes(equipment)) {
      onUpdate({ equipment: current.filter(e => e !== equipment) });
    } else {
      onUpdate({ equipment: [...current, equipment] });
    }
  };

  return (
    <div className="space-y-8">
      <VoiceHint 
        message="Time to optimize your kitchen setup! Tell me about your household and equipment so I can create recipes that work perfectly for your space."
        voice="medium"
      />

      {/* Household Size */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Household Size
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {[1, 2, 3, 4, 5, 6].map((size) => (
                <button
                  key={size}
                  onClick={() => onUpdate({ householdSize: size })}
                  className={`p-3 border rounded-lg text-center transition-colors ${
                    profile.householdSize === size
                      ? "bg-sage text-sage-foreground border-sage"
                      : "desktop-hover:bg-muted"
                  }`}
                >
                  <div className="text-lg font-medium">{size}</div>
                  <div className="text-xs text-muted-foreground">
                    {size === 1 ? "person" : "people"}
                  </div>
                </button>
              ))}
            </div>
            
            {profile.householdSize > 4 && (
              <VoiceHint 
                message="Cooking for a crowd! I'll make sure to include family-friendly recipes with easy scaling options."
                voice="medium"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Kitchen Equipment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ChefHat className="h-4 w-4" />
            Kitchen Equipment
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Select the equipment you have available
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {equipmentOptions.map((equipment) => (
              <div
                key={equipment.id}
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  profile.equipment?.includes(equipment.id)
                    ? "bg-sage/10 border-sage/20"
                    : "desktop-hover:bg-muted"
                }`}
                onClick={() => toggleEquipment(equipment.id)}
              >
                <Checkbox
                  checked={profile.equipment?.includes(equipment.id)}
                  onChange={() => {}} // Handled by div click
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <IconWrapper icon={equipment.icon} />
                    <Label className="text-sm cursor-pointer">
                      {equipment.label}
                    </Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Batch Prep Preference */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Batch Prep Preference
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            How much meal prep work do you want to do?
          </p>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={profile.batchPrep}
            onValueChange={(value) => onUpdate({ batchPrep: value })}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {[
              { 
                value: "none", 
                label: "No Batch Prep", 
                desc: "Cook fresh each time",
                icon: getBatchPrepIcon("none")
              },
              { 
                value: "light", 
                label: "Light Prep", 
                desc: "Some ingredients prepped ahead",
                icon: getBatchPrepIcon("light")
              },
              { 
                value: "full", 
                label: "Full Batch Prep", 
                desc: "Maximum efficiency & planning",
                icon: getBatchPrepIcon("full")
              }
            ].map(({ value, label, desc, icon }) => (
              <div key={value} className="flex items-center space-x-2 border rounded-lg p-4">
                <RadioGroupItem value={value} id={`batch-${value}`} />
                <div className="flex-1">
                  <Label htmlFor={`batch-${value}`} className="text-sm font-medium flex items-center gap-2">
                    <IconWrapper icon={icon} />
                    {label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Family Scaling Default */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Default Family Scaling</CardTitle>
          <p className="text-sm text-muted-foreground">
            When creating family-sized portions, multiply recipes by:
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((multiplier) => (
                <button
                  key={multiplier}
                  onClick={() => onUpdate({ familyScaling: multiplier })}
                  className={`p-3 border rounded-lg text-center transition-colors ${
                    profile.familyScaling === multiplier
                      ? "bg-sage text-sage-foreground border-sage"
                      : "desktop-hover:bg-muted"
                  }`}
                >
                  <div className="text-lg font-medium">×{multiplier}</div>
                  <div className="text-xs text-muted-foreground">
                    {multiplier === 1 ? "Standard" : 
                     multiplier === 2 ? "Double" :
                     multiplier === 3 ? "Triple" : "Quadruple"}
                  </div>
                </button>
              ))}
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Perfect for meal prep, entertaining, or feeding larger families
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Equipment-specific Hint */}
      {profile.equipment?.includes('instant-pot') && profile.equipment?.includes('air-fryer') && (
        <VoiceHint 
          message="Instant Pot AND Air Fryer? You're all set for some serious meal prep magic! I'll include combo recipes that use both."
          voice="high"
        />
      )}
    </div>
  );
}